import { useState, useEffect } from 'react';
import { ArrowRight, Phone } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SolutionScenario } from '@/types/scenarios/solution';

// Map solution slugs to custom brand icons (same as dropdown)
const solutionIcons: Record<string, string> = {
  'automotive': '/icons/solutions/icon-automotive.png',
  'marine': '/icons/solutions/icon-marine.png',
  'equipment': '/icons/solutions/icon-industrial-equipment.png',
  'appliances': '/icons/solutions/icon-appliances.png',
  'office-equipment': '/icons/solutions/icon-office-equipment.png',
  'home-builders': '/icons/solutions/icon-home-builders.png',
  'travel-hospitality': '/icons/solutions/icon-travel-hospitality.png',
  'manufacturing': '/icons/solutions/icon-manufacturing.png',
  'industrial': '/icons/solutions/icon-industrial.png',
  'mining': '/icons/solutions/icon-mining.png',
  'trades': '/icons/solutions/icon-trades.png',
  'plumbing': '/icons/solutions/icon-plumbing.png',
  'repair-services': '/icons/solutions/icon-repair-services.png',
  'real-estate': '/icons/solutions/icon-real-estate.png',
  'events': '/icons/solutions/icon-events.png',
};

// Ken Burns animation variants
const kenBurnsVariants = [
  { scale: [1, 1.04], x: [0, -8], y: [0, -5] },
  { scale: [1, 1.05], x: [0, 8], y: [0, -4] },
  { scale: [1, 1.03], x: [0, -5], y: [0, 6] },
  { scale: [1, 1.04], x: [0, 6], y: [0, 4] },
];

interface SolutionHeroProps {
  scenario: SolutionScenario;
  heroImage: string;
  onGetStarted: () => void;
  onTalkToMe: () => void;
  isCreating?: boolean;
}

export const SolutionHero = ({
  scenario,
  heroImage,
  onGetStarted,
  onTalkToMe,
  isCreating,
}: SolutionHeroProps) => {
  const [imageIndex, setImageIndex] = useState(0);

  // Rotate images every 8 seconds (for Ken Burns effect)
  useEffect(() => {
    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % kenBurnsVariants.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const marketIconSrc = solutionIcons[scenario.meta.slug];

  // Hero copy comes from the scenario file. Two render modes:
  //   - Split (gradient + rest): renders the gradient phrase with the shimmer treatment, then the rest in white
  //   - Plain: renders the whole headline in white
  const hasGradientSplit = !!(scenario.hero?.headlineGradient && scenario.hero?.headlineRest);
  const headlineText = scenario.hero?.headline ?? '';
  const subheadlineText = scenario.hero?.subheadline ?? scenario.meta.description;

  return (
    <>
      <section className="relative w-full h-[65vh] sm:h-[78vh] md:h-[84vh] min-h-[480px] sm:min-h-[600px] md:min-h-[660px] flex items-center text-white overflow-hidden">
        {/* Background Image with Ken Burns */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence mode="sync">
            <motion.img
              key={imageIndex}
              src={heroImage}
              alt={`${scenario.meta.name} hero background`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1 }}
              animate={{
                opacity: 1,
                scale: kenBurnsVariants[imageIndex].scale,
                x: kenBurnsVariants[imageIndex].x,
                y: kenBurnsVariants[imageIndex].y,
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.2, ease: 'easeInOut' },
                scale: { duration: 8, ease: 'linear' },
                x: { duration: 8, ease: 'linear' },
                y: { duration: 8, ease: 'linear' },
              }}
              onError={(e) => {
                e.currentTarget.src = "/hero-background.webp";
              }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/70 z-10" />
        </div>

        {/* Main content - Centered */}
        <div className="relative z-10 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl">
            {/* Market icon - large and prominent */}
            {marketIconSrc && (
              <div className="mb-4">
                <img
                  src={marketIconSrc}
                  alt={scenario.meta.market}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain mx-auto drop-shadow-2xl"
                />
              </div>
            )}

            {/* Market tag */}
            <div className="mb-6 inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full">
              <span className="text-sm sm:text-base font-semibold text-white">
                {scenario.meta.market}
              </span>
            </div>

            {/* Main headline — driven by scenario.hero. Each phrase renders as its own block line so wrapping is predictable. */}
            {hasGradientSplit ? (
              <h1
                className="font-bold leading-[0.95] drop-shadow-2xl mb-6"
                style={{ fontSize: 'clamp(2.25rem, 5.2vw, 5.5rem)' }}
              >
                <span
                  className="block bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%]"
                  style={{
                    backgroundImage:
                      'linear-gradient(110deg, #60a5fa 0%, #a855f7 25%, #ec4899 50%, #a855f7 75%, #60a5fa 100%)',
                  }}
                >
                  {scenario.hero!.headlineGradient}
                </span>
                <span className="block text-white">{scenario.hero!.headlineRest}</span>
              </h1>
            ) : (
              <h1
                className="font-bold leading-[0.95] drop-shadow-2xl mb-6 text-white"
                style={{ fontSize: 'clamp(2.25rem, 5.2vw, 5.5rem)' }}
              >
                {headlineText}
              </h1>
            )}

            <p
              className="mt-6 text-white font-normal drop-shadow-lg max-w-5xl mx-auto"
              style={{ fontSize: 'clamp(1.125rem, 1.75vw, 1.5rem)' }}
            >
              {subheadlineText}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={onGetStarted}
                disabled={isCreating}
                className="rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-lg hover:from-purple-700 hover:to-blue-700 transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8"
                style={{ fontSize: 'clamp(1rem, 1.2vw, 1.25rem)', padding: 'clamp(0.875rem, 1vw, 1rem) clamp(1.5rem, 2vw, 2rem)' }}
              >
                {isCreating ? "Getting Started..." : "Start Free"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={onTalkToMe}
                className="rounded-lg bg-white/10 text-white border-2 border-white/50 hover:bg-white/20 hover:border-white font-semibold shadow-lg backdrop-blur-sm transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8"
                style={{ fontSize: 'clamp(1rem, 1.2vw, 1.25rem)', padding: 'clamp(0.875rem, 1vw, 1rem) clamp(1.5rem, 2vw, 2rem)' }}
              >
                <Phone className="mr-2 h-5 w-5" />
                Talk To Me
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Shimmer animation styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};
